import {forwardRef, HTMLInputTypeAttribute, InputHTMLAttributes, ReactNode, useState} from 'react'
import Label from '@components/ui/Label'
import {
    StyledInput,
    StyledInputContainer,
    StyledInputGrid,
    StyledInputWrapper,
    StyledVisibleToggle
} from '@components/commons/input-text/style.ts'
import {EyeIcon, EyeOffIcon} from 'src/components/ui/icon'
import InputHelpText from '@components/ui/InputHelpText'
import {FieldError} from 'react-hook-form'
import InputStatusIcon from '@components/ui/InputStatusIcon'
import {DefaultTFuncReturn} from 'i18next'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    className?: string
    label?: string | DefaultTFuncReturn
    name?: string
    type: HTMLInputTypeAttribute
    typeIcon?: ReactNode
    copyAction?: boolean
    touched?: boolean
    error?: FieldError
    /**
     * how to use errorMessage with translations
     *  errorMessage={t(errors.text?.message || '')} this avoid undefined value problems
     */
    errorMessage?: string | DefaultTFuncReturn
    visibilityToggle?: boolean
    helpText?: string | DefaultTFuncReturn
    placeholder?: string
    inputSize?: 'sm' | 'md' | 'lg'
}

const InputText = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            name,
            type,
            typeIcon,
            /*copyAction,*/
            touched = false,
            errorMessage,
            helpText,
            className,
            disabled,
            placeholder,
            visibilityToggle = false,
            inputSize = 'md',
            ...rest
        },
        ref
    ) => {
        const [eyeVisible, setEyeVisible] = useState(false)

        return (
            <StyledInputContainer className={className}>
                {label && <Label htmlFor={name}>{label}</Label>}
                <StyledInputGrid>
                    <StyledInputWrapper
                        touched={touched}
                        hasError={!!errorMessage}
                        disabled={disabled}
                        size={inputSize}
                        visibilityToggle={visibilityToggle}
                    >
                        {typeIcon}
                        <StyledInput
                            ref={ref}
                            name={name}
                            type={eyeVisible ? 'text' : type}
                            placeholder={placeholder}
                            disabled={disabled}
                            {...rest}
                        />
                        {/*input status icon*/}
                        <InputStatusIcon touched={touched} hasError={!!errorMessage} />
                    </StyledInputWrapper>

                    {/*input Action toggle*/}
                    {visibilityToggle && (
                        <StyledVisibleToggle
                            size={inputSize}
                            disabled={disabled}
                            onClick={() => setEyeVisible(!eyeVisible)}
                        >
                            {eyeVisible ? <EyeIcon size={20} data-color /> : <EyeOffIcon size={20} data-color />}
                        </StyledVisibleToggle>
                    )}
                </StyledInputGrid>
                {/*help text*/}
                <InputHelpText error={errorMessage} helpText={helpText} />
            </StyledInputContainer>
        )
    }
)

export default InputText

InputText.displayName = 'InputText'
